<template>
    <div v-if="embedSrc" class="embed">
        <iframe
            :src="embedSrc"
            title="YouTube video"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            referrerpolicy="strict-origin-when-cross-origin"
        />
    </div>
</template>

<script setup lang="ts">
import type { RelationNodeProps } from "directus-extension-flexible-editor/content";

const props = defineProps<{
    id: RelationNodeProps["id"];
    junction: RelationNodeProps["junction"];
    collection: RelationNodeProps["collection"];
    data?: RelationNodeProps["data"];
}>();

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

const parseStartSeconds = (value: string | null) => {
    if (!value) return undefined;

    const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
    if (match && (match[1] || match[2] || match[3])) {
        const hours = Number(match[1] || 0);
        const minutes = Number(match[2] || 0);
        const seconds = Number(match[3] || 0);
        return hours * 3600 + minutes * 60 + seconds;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
};

const parseYouTube = (link?: string) => {
    if (!link) return null;

    let url: URL;
    try {
        url = new URL(link);
    } catch {
        return null;
    }

    const host = url.hostname.replace(/^www\./, "");
    if (!["youtube.com", "m.youtube.com", "music.youtube.com", "youtu.be", "youtube-nocookie.com"].includes(host)) {
        return null;
    }

    let id: string | null = null;

    if (host === "youtu.be") {
        id = url.pathname.split("/").filter(Boolean)[0] ?? null;
    } else {
        const segments = url.pathname.split("/").filter(Boolean);
        if (url.searchParams.get("v")) {
            id = url.searchParams.get("v");
        } else if (["embed", "shorts", "live", "v"].includes(segments[0])) {
            id = segments[1] ?? null;
        }
    }

    if (!id || !VIDEO_ID_RE.test(id)) return null;

    const start = parseStartSeconds(url.searchParams.get("start") || url.searchParams.get("t"));
    return { id, start };
};

const embedSrc = computed(() => {
    const parsed = parseYouTube(props.data?.youtube_link);
    if (!parsed) return null;

    const params = new URLSearchParams({
        rel: "0",
        iv_load_policy: "3",
        playsinline: "1",
    });
    if (parsed.start) params.set("start", String(parsed.start));

    return `https://www.youtube-nocookie.com/embed/${parsed.id}?${params.toString()}`;
});
</script>

<style lang="scss" scoped>
.embed {
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #fafafa;

    iframe {
        width: 100%;
        height: 100%;
        border: 0;
        display: block;
    }
}
</style>
